import React, { useState } from 'react';
import { Phone, Mail, Clock, MapPin } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    serviceType: '',
    timeline: '',
    details: ''
  });

  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message || 'Thank you! We will contact you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try calling us directly.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);
    
    try {
      const response = await axios.post(`${API}/quote`, {
        name: quoteForm.name,
        email: quoteForm.email,
        phone: quoteForm.phone,
        project_type: quoteForm.projectType,
        service_type: quoteForm.serviceType,
        timeline: quoteForm.timeline,
        details: quoteForm.details
      });
      toast.success(response.data.message || 'Quote request received! We will get back to you within 24 hours.');
      setQuoteForm({ name: '', email: '', phone: '', projectType: '', serviceType: '', timeline: '', details: '' });
    } catch (error) {
      console.error('Quote form error:', error);
      toast.error('Failed to send quote request. Please try calling us directly.');
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/6474207/pexels-photo-6474207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Contact Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Get In Touch</h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Ready to start your project? Contact us for a free consultation and quote
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Call Us</h3>
                <a href="tel:4036044218" className="text-gray-600 hover:text-[#00AEEF] transition-colors block mb-1">
                  403-604-4218
                </a>
                <a href="tel:4034703018" className="text-gray-600 hover:text-[#00AEEF] transition-colors block">
                  403-470-3018
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Email Us</h3>
                <a href="mailto:info@sat-drywall.com" className="text-gray-600 hover:text-[#00AEEF] transition-colors">
                  info@sat-drywall.com
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Business Hours</h3>
                <p className="text-gray-600 text-sm">Mon-Fri: 8AM - 6PM</p>
                <p className="text-gray-600 text-sm">Sat: 9AM - 4PM</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quote Request Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Request a Free Quote</h2>
              <p className="text-lg text-gray-600">
                Get a detailed estimate for your project - no obligation
              </p>
            </div>

            <Card className="border-none shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <form onSubmit={handleQuoteSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                      <Input
                        required
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                        placeholder="Your full name"
                        className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        required
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        placeholder="your@email.com"
                        className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                      <Input
                        type="tel"
                        required
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        placeholder="(403) 000-0000"
                        className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Project Type *</label>
                      <select
                        required
                        value={quoteForm.projectType}
                        onChange={(e) => setQuoteForm({ ...quoteForm, projectType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00AEEF] focus:border-[#00AEEF]"
                      >
                        <option value="">Select project type</option>
                        <option value="commercial">Commercial</option>
                        <option value="residential">Residential</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Service Needed *</label>
                      <select
                        required
                        value={quoteForm.serviceType}
                        onChange={(e) => setQuoteForm({ ...quoteForm, serviceType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00AEEF] focus:border-[#00AEEF]"
                      >
                        <option value="">Select service</option>
                        <option value="drywall">Drywall</option>
                        <option value="mudtape">Mud Tape</option>
                        <option value="sprayfoam">Spray Foam Insulation</option>
                        <option value="insulation">Insulation</option>
                        <option value="blowinsulation">Blow Insulation</option>
                        <option value="multiple">Multiple Services</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Timeline</label>
                      <select
                        value={quoteForm.timeline}
                        onChange={(e) => setQuoteForm({ ...quoteForm, timeline: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00AEEF] focus:border-[#00AEEF]"
                      >
                        <option value="">Select timeline</option>
                        <option value="asap">As soon as possible</option>
                        <option value="1month">Within 1 month</option>
                        <option value="3months">Within 3 months</option>
                        <option value="flexible">Flexible</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project Details</label>
                    <Textarea
                      value={quoteForm.details}
                      onChange={(e) => setQuoteForm({ ...quoteForm, details: e.target.value })}
                      placeholder="Please provide details about your project (size, scope, special requirements, etc.)"
                      rows={5}
                      className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmittingQuote}
                    className="w-full bg-[#00AEEF] hover:bg-[#0096d1] text-white py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingQuote ? 'Sending...' : 'Request Free Quote'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
              <p className="text-lg text-gray-600">
                Have questions? We're here to help!
              </p>
            </div>

            <Card className="border-none shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                      <Input
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Your name"
                        className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="your@email.com"
                        className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                    <Input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="(403) 000-0000"
                      className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                    <Textarea
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="How can we help you?"
                      rows={6}
                      className="border-gray-300 focus:border-[#00AEEF] focus:ring-[#00AEEF]"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmittingContact}
                    className="w-full bg-[#00AEEF] hover:bg-[#0096d1] text-white py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingContact ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
